// Topbar ที่ใช้ร่วมกันทุกหน้า (แทน renderTopbar() เดิม)
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { sessionInitials, type WazzupSession } from "@features/auth/lib/session";
import { ssoLogout } from "@features/auth/lib/iam-sso";
import { canSeeAdminMenus } from "@features/auth/lib/roles";
import { sessionCleared } from "@features/auth/model/session-slice";
import { IconBell, IconSearch, IconSignOut } from "@shared/ui/icons";
import { cn } from "@shared/lib/utils";
import { GlobalMenuSlot } from "./global-menu-slot";

type Props = { active?: "myjob" | "corporate" | "setting" };

export function Topbar({ active }: Props) {
  const session = useAppSelector((s) => s.session.current);
  const profileTitle = session
    ? session.empThaiName || session.empEngName || "Signed in"
    : "Profile (ยังไม่ได้เชื่อมต่อบัญชี — login ได้ที่ Setting → Staff)";

  return (
    <header className="topbar" id="topbar">
      <GlobalMenuSlot />
      <Link className="logo" to="/">
        <img className="logo-img" src="/img/logo.png" alt="Far East Fame Line" />
        <span className="logo-name">Agency Intelligence</span>
      </Link>
      <nav className="top-nav">
        <Link to="/" className={active === "myjob" ? "active" : ""}>
          My Job
        </Link>
        {/* ตัวเลขระดับบริษัททุกเพจ — เปิดให้ Admin เท่านั้นเหมือนหน้า Setting */}
        {canSeeAdminMenus(session) && (
          <Link to="/corporate-performance" className={active === "corporate" ? "active" : ""}>
            Corporate Performance
          </Link>
        )}
        {/* คนที่ถูกจำกัดสิทธิ์กดเข้าไปก็เจอหน้า "ไม่มีสิทธิ์" — ไม่ต้องโชว์เมนูให้สับสน */}
        {canSeeAdminMenus(session) && (
          <Link to="/setting/product" className={active === "setting" ? "active" : ""}>
            Setting
          </Link>
        )}
      </nav>
      <div className="spacer" />
      <div className="search-box">
        <IconSearch />
        <input type="text" placeholder="Search..." />
      </div>
      <button type="button" className="icon-btn" title="Notifications">
        <IconBell />
      </button>
      <ProfileMenu title={profileTitle} />
    </header>
  );
}

/**
 * รูปโปรไฟล์ = ปุ่มเปิดเมนู logout — ต้องออกจากระบบได้จากทุกหน้า
 * ไม่ใช่ต้องเดินไปที่ Setting → Staff ก่อน
 */
function ProfileMenu({ title }: { title: string }) {
  const session = useAppSelector((s) => s.session.current);
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="profile-wrap" ref={wrap}>
      <button
        type="button"
        className="profile"
        id="profile"
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <ProfileAvatar />
      </button>

      <div className={cn("profile-menu", open && "open")}>
        {session ? (
          <>
            <div className="profile-menu-head">
              <span className="profile-menu-pic">
                <ProfileAvatar />
              </span>
              <div className="profile-menu-id">
                {/* ชื่อไทยเป็นตัวหลักเหมือนระบบอื่นในเครือ — ยังไม่มีค่อยถอยไปใช้ชื่ออังกฤษ */}
                <div className="profile-menu-name">
                  <b>{session.empThaiName || session.empEngName || "ผู้ใช้"}</b>
                  {/* รหัสพนักงานคือ key ที่ผูกสิทธิ์ทุกอย่าง — เทียบกับรายการ Staff ได้ตรงตัว
                      คนที่มีหลายรหัสจะได้รู้ว่าตอนนี้ใช้ใบไหนอยู่ */}
                  {session.empCode ? (
                    <span className="code-chip">{session.empCode}</span>
                  ) : (
                    <span className="code-chip code-chip-none">ไม่รู้รหัส</span>
                  )}
                </div>
                <div className="muted small-note">{subtitleOf(session)}</div>
              </div>
            </div>
            <RoleList session={session} />
            <button type="button" onClick={() => dispatch(sessionCleared())}>
              <IconSignOut size={18} />
              ออกจากระบบ
            </button>
            {/* มาทาง SSO ต้องล้าง cookie ที่ IAM ด้วย ไม่งั้นกดเข้าใหม่จะเข้าเงียบ ๆ ทันที */}
            {session.via === "sso" && (
              <button type="button" onClick={() => ssoLogout(() => dispatch(sessionCleared()))}>
                <IconSignOut size={18} />
                ออกจากระบบทุกระบบ (SSO)
              </button>
            )}
          </>
        ) : (
          <Link to="/login">เข้าสู่ระบบ</Link>
        )}
      </div>
    </div>
  );
}

/**
 * สิทธิที่ได้จาก SSO — มาจาก claim role ใน token และถูกทับด้วย userRole จาก
 * /wazzup/profile เมื่อโปรไฟล์มาถึง (ดู use-session-profile)
 *
 * แสดงตามที่ IAM ให้มาจริง ๆ ไม่แปลชื่อ role เป็นภาษาไทย — ชื่อพวกนี้เป็นสิ่งที่
 * แอดมินตั้งไว้ที่ IAM คนดูจะเทียบกับหน้าจัดการสิทธิที่นั่นได้ตรงตัว
 */
function RoleList({ session }: { session: WazzupSession }) {
  const roles = session.roles ?? [];

  return (
    <div className="profile-menu-roles">
      <div className="muted small-note">สิทธิจาก SSO</div>
      {roles.length ? (
        <div className="role-chips">
          {roles.map((r) => (
            <span key={r} className="code-chip">
              {r}
            </span>
          ))}
        </div>
      ) : (
        // ไม่มี role ไม่ใช่ error — บางบัญชีเข้าได้แต่ไม่ได้ถูกกำหนดสิทธิพิเศษไว้
        <div className="muted small-note">
          {session.via === "sso" ? "ยังไม่ได้รับสิทธิเฉพาะจาก IAM" : "ไม่ได้เข้าผ่าน SSO"}
        </div>
      )}
    </div>
  );
}

/**
 * บรรทัดรองใต้ชื่อ — ตำแหน่ง · แผนก เหมือนเมนูของ Inbox Center
 * ยังไม่รู้ตำแหน่ง (โปรไฟล์ยังไม่มา / ดึงไม่ได้) ค่อยบอกว่าเข้ามาทางไหนแทน
 */
function subtitleOf(s: WazzupSession): string {
  const parts = [s.positionName, s.departmentName].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return s.via === "sso" ? "เข้าสู่ระบบด้วย SSO" : "เข้าสู่ระบบด้วยรหัสพนักงาน";
}

function ProfileAvatar() {
  const session = useAppSelector((s) => s.session.current);
  const [imgFailed, setImgFailed] = useState(false);

  // รูปโปรไฟล์มาจาก Wazzup และหลุดได้ — ของเดิมลบ <img> ทิ้งจนวงกลมว่างเปล่า
  // อันนี้ถอยไปใช้ตัวย่อแทน จะได้ไม่เห็นวงกลมเปล่า
  if (session?.profileURL && !imgFailed) {
    return <img src={session.profileURL} alt="profile" onError={() => setImgFailed(true)} />;
  }
  return <span>{sessionInitials(session)}</span>;
}
